/* Dabi Deri - Quick View
   Tetikleyici: [data-qv-open] (data-product-url veya data-product-handle tasir)
   Icerik: /products/<handle>?section_id=dabi-quick-view
   Erisilebilirlik: dialog semantigi, focus trap, ESC, focus iadesi, scroll kilidi. */
(() => {
  const SECTION_ID = 'dabi-quick-view';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const modal = document.querySelector('[data-qv-modal]');
  if (!modal) return;

  const dialog = modal.querySelector('[data-qv-dialog]');
  const body = modal.querySelector('[data-qv-body]');
  const loadingTpl = modal.querySelector('[data-qv-loading]')?.outerHTML || '';

  let lastFocused = null;
  let variants = [];
  let abortCtrl = null;
  let galleryTimer = null;

  /* ---------- acilis / kapanis ---------- */

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) clearInterval(galleryTimer);
  });

  const lockScroll = (on) => {
    // iOS Safari html'de overflow:hidden'i dinlemez; govde sabitlenir, konum saklanir.
    const html = document.documentElement;
    if (on) {
      html.style.setProperty('--dabi-lock-top', `${-window.scrollY}px`);
      html.dataset.dabiLockY = String(window.scrollY);
      html.classList.add('dabi-scroll-locked');
    } else {
      const y = Number(html.dataset.dabiLockY || 0);
      html.classList.remove('dabi-scroll-locked');
      html.style.removeProperty('--dabi-lock-top');
      window.scrollTo(0, y);
    }
  };

  const open = async (url) => {
    lastFocused = document.activeElement;
    modal.hidden = false;
    // Reflow'u zorla, sonra sinifi ver: requestAnimationFrame arka plan sekmelerinde
    // tetiklenmeyebiliyor ve modal acik ama gorunmez kaliyordu.
    void modal.offsetWidth;
    modal.classList.add('is-open');
    lockScroll(true);
    dialog.setAttribute('aria-busy', 'true');
    body.innerHTML = loadingTpl;
    dialog.focus();

    try {
      abortCtrl?.abort();
      abortCtrl = new AbortController();
      const sep = url.includes('?') ? '&' : '?';
      const res = await fetch(`${url}${sep}section_id=${SECTION_ID}`, { signal: abortCtrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      body.innerHTML = await res.text();
      dialog.setAttribute('aria-busy', 'false');
      hydrate();
      // Ilk anlamli odak: kapat butonu degil, baslik sonrasi ilk aksiyon
      (body.querySelector('[data-qv-atc]') || dialog).focus({ preventScroll: true });
    } catch (err) {
      if (err.name === 'AbortError') return;
      body.innerHTML =
        '<p class="qv-modal__error">Ürün bilgisi yüklenemedi. Lütfen tekrar deneyin.</p>';
      dialog.setAttribute('aria-busy', 'false');
    }
  };

  const close = () => {
    stopGallery();
    abortCtrl?.abort();
    modal.classList.remove('is-open');
    lockScroll(false);
    const finish = () => {
      modal.hidden = true;
      body.innerHTML = '';
      lastFocused?.focus?.();
      lastFocused = null;
    };
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) finish();
    else setTimeout(finish, 220);
  };

  /* ---------- icerik davranislari ---------- */

  function hydrate() {
    const data = body.querySelector('[data-qv-variants]');
    try {
      variants = data ? JSON.parse(data.textContent) : [];
    } catch {
      variants = [];
    }

    // Galeri
    body.querySelectorAll('[data-qv-thumb]').forEach((btn) => {
      btn.addEventListener('click', () => {
        stopGallery();
        showImage(btn.dataset.qvThumb);
      });
    });

    startGallery();
    setupCursorNav();

    // Varyant secimi
    body.querySelectorAll('[data-qv-option-input]').forEach((input) => {
      input.addEventListener('change', updateVariant);
    });

    // Adet
    body.querySelectorAll('[data-qv-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = body.querySelector('[data-qv-qty-input]');
        const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.qvQty));
        input.value = next;
      });
    });

    // Sepete ekle
    body.querySelector('[data-qv-form], #dabi-qv-form, form')?.addEventListener('submit', addToCart);
  }

  /* Sahne uzerinde imleci takip eden yon dugmesi. Farenin sag yarida mi sol
     yarida mi oldugu yonu belirler; tiklama o yone gecirir. Klavye ve
     dokunma icin kucuk gorseller zaten var, bu yalnizca fare zenginlestirmesi. */
  function setupCursorNav() {
    const stage = body.querySelector('.qv__stage');
    const images = Array.from(body.querySelectorAll('[data-qv-image]'));
    if (!stage || images.length < 2) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    stage.setAttribute('data-qv-nav', '');

    const cursor = document.createElement('span');
    cursor.className = 'qv__cursor';
    cursor.setAttribute('aria-hidden', 'true');
    cursor.innerHTML =
      '<svg viewBox="0 0 24 24" focusable="false"><path d="M5 12h13M14 7l5 5-5 5"/></svg>';
    stage.appendChild(cursor);

    const current = () => images.findIndex((img) => img.classList.contains('is-active'));

    const place = (event) => {
      const box = stage.getBoundingClientRect();
      const x = event.clientX - box.left;
      cursor.style.left = `${x}px`;
      cursor.style.top = `${event.clientY - box.top}px`;
      cursor.dataset.direction = x < box.width / 2 ? 'prev' : 'next';
      cursor.classList.add('is-visible');
    };

    stage.addEventListener('pointerenter', place);
    stage.addEventListener('pointermove', place);
    stage.addEventListener('pointerleave', () => cursor.classList.remove('is-visible'));

    stage.addEventListener('click', (event) => {
      if (event.target.closest('a, button') && !event.target.closest('.qv__cursor')) return;
      const box = stage.getBoundingClientRect();
      const forward = event.clientX - box.left >= box.width / 2;
      const index = current();
      const next = (index + (forward ? 1 : -1) + images.length) % images.length;
      stopGallery();
      showImage(String(next));
    });
  }

  /* Urun penceresi acikken diger gorseller kendiliginden gecer. Uzerine
     gelindiginde ya da bir kucuk gorsele basildiginda durur; boylece
     musteri bakmak istedigi kareyi kaybetmez. */
  function stopGallery() {
    if (galleryTimer === null) return;
    clearInterval(galleryTimer);
    galleryTimer = null;
  }

  function startGallery() {
    stopGallery();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const stage = body.querySelector('[data-qv-image]')?.parentElement;
    const images = Array.from(body.querySelectorAll('[data-qv-image]'));
    if (images.length < 2) return;

    // Tema ayari 0 ise kendiliginden gecmez. `|| 4000` yazilsa 0 da varsayilana
    // dusecegi icin deger ayrica sinaniyor.
    const raw = body.querySelector('[data-qv-content]')?.dataset.qvGallerySpeed;
    const interval = raw === undefined || raw === '' ? 4000 : Number(raw);
    if (!Number.isFinite(interval) || interval < 500) return;

    let index = 0;

    galleryTimer = setInterval(() => {
      index = (index + 1) % images.length;
      showImage(String(index));
    }, interval);

    stage?.addEventListener('pointerenter', stopGallery);
    body.querySelector('.qv__thumbs')?.addEventListener('pointerenter', stopGallery);
  }

  function showImage(index) {
    body.querySelectorAll('[data-qv-image]').forEach((img) => {
      img.classList.toggle('is-active', img.dataset.qvImage === index);
    });
    body.querySelectorAll('[data-qv-thumb]').forEach((btn) => {
      const active = btn.dataset.qvThumb === index;
      btn.classList.toggle('is-active', active);
      btn.toggleAttribute('aria-current', active);
    });
  }

  function updateVariant() {
    const chosen = [...body.querySelectorAll('[data-qv-option-input]:checked')].map((i) => i.value);
    const match = variants.find((v) => v.options.every((opt, i) => opt === chosen[i]));

    const idInput = body.querySelector('[data-qv-variant-id]');
    const atc = body.querySelector('[data-qv-atc]');
    const atcText = atc?.querySelector('.qv__atc-text');
    const priceBox = body.querySelector('[data-qv-price]');

    // Secili deger etiketleri
    body.querySelectorAll('[data-qv-option-value]').forEach((el, i) => {
      if (chosen[i]) el.textContent = chosen[i];
    });

    if (!match) {
      if (atc) atc.disabled = true;
      if (atcText) atcText.textContent = atcText.dataset.qvUnavailable || window.variantStrings?.unavailable || '';
      return;
    }

    if (idInput) idInput.value = match.id;
    if (atc) atc.disabled = !match.available;
    // Etiketler pencereyle birlikte geliyor (yakinda / tukendi ayrimi orada yapiliyor).
    if (atcText) atcText.textContent = match.available ? (atcText.dataset.qvAdd || window.variantStrings?.addToCart || '') : (atcText.dataset.qvSoldOut || window.variantStrings?.soldOut || '');

    if (priceBox) {
      const money = (cents) =>
        window.Shopify?.formatMoney
          ? window.Shopify.formatMoney(cents, window.Shopify.money_format)
          : (cents / 100).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
      priceBox.innerHTML =
        match.compare_at_price > match.price
          ? `<span class="qv__price-now">${money(match.price)}</span><s class="qv__price-was">${money(
              match.compare_at_price
            )}</s>`
          : `<span class="qv__price-now">${money(match.price)}</span>`;
    }
  }

  async function addToCart(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const atc = body.querySelector('[data-qv-atc]');
    const feedback = body.querySelector('[data-qv-feedback]');
    if (!atc || atc.disabled) return;

    atc.classList.add('is-loading');
    atc.disabled = true;
    if (feedback) feedback.textContent = '';

    try {
      const formData = new FormData(form);
      const res = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Sepete eklenemedi');

      atc.classList.remove('is-loading');
      atc.classList.add('is-success');
      if (feedback) feedback.textContent = 'Ürün sepete eklendi.';

      // Cart drawer'i tazele ve ac
      document.dispatchEvent(new CustomEvent('dabi:cart:added', { detail: data }));
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        setTimeout(() => {
          close();
          drawer.open();
        }, 550);
      } else {
        setTimeout(() => {
          atc.classList.remove('is-success');
          atc.disabled = false;
        }, 1600);
      }
    } catch (err) {
      atc.classList.remove('is-loading');
      atc.disabled = false;
      if (feedback) feedback.textContent = err.message || 'Sepete eklenemedi.';
    }
  }

  /* ---------- global olaylar ---------- */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-qv-open]');
    if (trigger) {
      const url = trigger.dataset.productUrl;
      if (!url) return;
      event.preventDefault();
      open(url);
      return;
    }
    if (event.target.closest('[data-qv-close]')) close();
  });

  document.addEventListener('keydown', (event) => {
    if (modal.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    const items = [...dialog.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
})();
